import { useEffect, useMemo, useRef, useState } from 'react';

/** Base key for the "played once" flag; the observed organization id is appended per org. */
export const FRAUD_BORDER_SWEEP_SESSION_KEY = 'fraud-review-border-sweep-played';

/** Builds the per-organization sessionStorage key so the sweep replays for a different org. */
export const getFraudBorderSweepSessionKey = (organizationId?: string | number): string =>
  `${FRAUD_BORDER_SWEEP_SESSION_KEY}-${organizationId ?? 'unknown'}`;

/**
 * Fraction of the card that must be visible before the animation is allowed to start.
 * Kept just under 1 because sub-pixel rounding means a "fully visible" element often reports a
 * ratio slightly below 1.0, which would otherwise never satisfy a strict `>= 1` check.
 */
const FULLY_VISIBLE_THRESHOLD = 0.95;

/** Total animation duration (4000ms sweep + 100ms fade tail), per the dev handoff. */
const ANIMATION_DURATION_MS = 4100;

interface UseFraudBorderSweepAnimationParams {
  /** Whether there is at least one flagged employee (animation never plays when false). */
  hasCount: boolean;
  /** Skip while the metric is still loading so the sweep lines up with the real value. */
  isLoading?: boolean;
  /** Observed organization id — the "played once" flag is scoped to it. */
  organizationId?: string | number;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Reads how long ago the sweep started for this session, in ms, or `undefined` if it never started.
 * We store the start timestamp (not a boolean) so a mid-flight remount — which happens when the
 * card is repacked into a different row as the dashboard data resolves — can resume the sweep for
 * its remaining time instead of being cut short (EL-33297).
 */
const getElapsedSinceStart = (key: string): number | undefined => {
  try {
    const startedAt = sessionStorage.getItem(key);
    if (!startedAt) {
      return undefined;
    }
    const parsed = Number(startedAt);
    if (!Number.isFinite(parsed)) {
      return undefined;
    }
    return Date.now() - parsed;
  } catch {
    return undefined;
  }
};

const markStartedThisSession = (key: string): void => {
  try {
    sessionStorage.setItem(key, `${Date.now()}`);
  } catch {
    // Ignore storage failures (e.g. private mode); the animation simply may replay.
  }
};

/**
 * Drives the one-time red border-sweep animation for the Fraud Review Required card.
 *
 * The animation:
 * - plays only when the card is fully visible in the viewport and there is at least one
 *   flagged employee (`hasCount`);
 * - starts on scroll if the card is below the fold on load (via IntersectionObserver);
 * - plays at most once per browser session (guarded by a start timestamp in sessionStorage);
 * - resumes for its remaining time if the card remounts mid-sweep (e.g. the layout moves it to a
 *   different row as the dashboard data resolves) instead of being cut short;
 * - is skipped entirely when the user prefers reduced motion.
 */
export const useFraudBorderSweepAnimation = ({
  hasCount,
  isLoading,
  organizationId,
}: UseFraudBorderSweepAnimationParams) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const hasTriggeredRef = useRef(false);
  // The pending "stop the sweep" timer is kept in a ref (not an effect-local variable) so it
  // survives effect re-runs. If it lived in the effect closure, any dependency change while the
  // sweep is in flight (e.g. isLoading toggling) would run the cleanup, clear the timer, and hit
  // the hasTriggered early-return without re-arming it — leaving isAnimating stuck true and the
  // border painted forever (EL-33221 follow-up).
  // @ts-ignore
  const stopTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const sessionKey = useMemo(() => getFraudBorderSweepSessionKey(organizationId), [organizationId]);

  useEffect(() => {
    const element = cardRef.current;

    const stopAfter = (remainingMs: number) => {
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
      }
      if (remainingMs <= 0) {
        setIsAnimating(false);
        return;
      }
      stopTimerRef.current = setTimeout(() => setIsAnimating(false), remainingMs);
    };

    // Once the sweep has been triggered this session we never observe again; instead we make sure
    // it is stopped at the right time. This runs on every effect re-run so a re-run mid-sweep
    // re-arms the stop timer for its remaining time rather than orphaning it.
    if (hasTriggeredRef.current) {
      const elapsed = getElapsedSinceStart(sessionKey);
      stopAfter(elapsed === undefined ? 0 : ANIMATION_DURATION_MS - elapsed);
      return undefined;
    }

    if (
      !element ||
      isLoading ||
      !hasCount ||
      prefersReducedMotion() ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    // The card remounts when the layout repacks it into a different row while the dashboard data
    // resolves. If the sweep already started this session, resume it for whatever time is left
    // rather than replaying from the top or (as before) dropping it entirely.
    const elapsed = getElapsedSinceStart(sessionKey);
    if (elapsed !== undefined) {
      hasTriggeredRef.current = true;
      const remaining = ANIMATION_DURATION_MS - elapsed;
      if (remaining > 0) {
        setIsAnimating(true);
      }
      stopAfter(remaining);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= FULLY_VISIBLE_THRESHOLD) {
          hasTriggeredRef.current = true;
          markStartedThisSession(sessionKey);
          setIsAnimating(true);
          observer.disconnect();

          stopAfter(ANIMATION_DURATION_MS);
        }
      },
      { threshold: [FULLY_VISIBLE_THRESHOLD] }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [hasCount, isLoading, sessionKey]);

  // Clear any pending stop timer only when the hook unmounts for good.
  useEffect(
    () => () => {
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
      }
    },
    []
  );

  return {
    cardRef,
    isAnimating,
  };
};

export default useFraudBorderSweepAnimation;
